<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <q-form ref="form">
      <div class="flex items-center justify-between">
        <h5 class="q-my-none q-mx-none q-pb-md">General Info</h5>
        <a href="https://wiki.swissre.com/pages/viewpage.action?pageId=569100259" target="_blank">Help (Wiki Link)</a>
      </div>
      <div class="row q-col-gutter-lg">
        <div class="col-4 new-ds-select" :class="{ error: apmIdError }">
          <service-select
            class="new-infra-apm-select"
            :error="apmIdError"
            onlyActive
            fullWidth
            :label="
              formData.digitalService?.digitalServiceName
                ? formData.digitalService.digitalServiceName
                : 'Choose your APMID *'
            "
            @reset="resetService"
            @addService="setService($event)"
          >
            <template v-slot:append>
              <q-icon name="help_outline" size="18px" class="cursor-pointer q-mr-xs">
                <q-tooltip
                  >Please choose an existing Digital Service (APMID) from the list. As it is not possible to create a
                  new APM ID via Engineering Portal, please use SNOW or DGF to create one.</q-tooltip
                >
              </q-icon>
            </template>
            <template v-slot:hint>
              <span class="service-select-hint"
                >It is not possible to create a new APM ID, please choose an existing one from the list. To create a new
                APM ID please use SNOW or <a href="https://dgf.swissre.com/" target="_blank">DGF</a></span
              >
            </template>
          </service-select>
        </div>
        <div class="col-4">
          <q-input
            v-model="formData.displayName"
            outlined
            :rules="[(val) => !!val || 'Field is required']"
            dense
            bg-color="white"
            label="Display name *"
          />
        </div>
        <div class="col-4">
          <q-input
            v-model="formData.subtitle"
            outlined
            :rules="[(val) => !!val || 'Field is required']"
            dense
            bg-color="white"
            label="Subtitle *"
          />
        </div>
        <div class="col-4">
          <q-select
            v-model="formData.category"
            option-value="value"
            :rules="[(val) => !!val || 'Field is required']"
            map-options
            option-label="label"
            dense
            bg-color="white"
            options-dense
            outlined
            label="Category *"
            emit-value
            :options="categories"
          />
        </div>
        <div class="col-4">
          <q-select
            :disable="!formData.category || !subCategories.length"
            option-value="value"
            map-options
            option-label="label"
            v-model="formData.subCategory"
            dense
            bg-color="white"
            options-dense
            emit-value
            outlined
            label="Sub category"
            :options="subCategories"
          />
        </div>
        <div class="col-4">
          <q-select
            :disable="!parentInfraServices.length"
            option-value="value"
            map-options
            option-label="label"
            v-model="formData.parentInfraService"
            dense
            bg-color="white"
            options-dense
            emit-value
            outlined
            label="Parent infra service"
            :options="parentInfraServices"
          >
            <template v-slot:append>
              <q-icon name="help_outline" size="18px" class="cursor-pointer q-mr-xs">
                <q-tooltip style="width: 380px"
                  >Please select Parent infra service in case service that you are creating has same APMID and will be
                  onboarded using same approach as Parent infra service Please leave Parent infra service as empty, in
                  case service that you are creating should be a parent and used for onboarding.
                </q-tooltip>
              </q-icon>
            </template></q-select
          >
        </div>
      </div>
      <div class="row q-col-gutter-lg">
        <div class="col-4">
          <q-select
            v-model="formData.cloudType"
            dense
            bg-color="white"
            emit-value
            map-options
            outlined
            multiple
            options-dense
            :rules="[(val) => !!val || 'Field is required']"
            label="Cloud type *"
            :options="cloudTypes"
          />
        </div>
        <div class="col-4">
          <q-select
            v-model="formData.lifecycleStage"
            dense
            options-dense
            bg-color="white"
            outlined
            emit-value
            map-options
            label="Lifecycle stage *"
            :rules="[(val) => !!val || 'Field is required']"
            :options="lifecycleStages"
          />
        </div>
      </div>
      <h5 class="q-my-none q-mx-none">Descriptions</h5>
      <div v-for="description in formData.descriptions" :key="description.id" class="row q-col-gutter-md q-mt-xs">
        <div class="col-12">
          <q-input bg-color="white" outlined dense v-model="description.type" disable />
        </div>
        <div class="col-6">
          <q-scroll-area
            :style="{ height: '165px' }"
            :thumb-style="{ borderRadius: '5px', background: '#9f9f9f', width: '2px', opacity: 1, right: 0 }"
          >
            <q-input
              type="textarea"
              :label="`${description.type} description`"
              bg-color="white"
              hint="Please, use markdown"
              v-model="description.text"
              :rules="[(val) => !!val || 'Field is required']"
              outlined
            ></q-input>
          </q-scroll-area>
        </div>
        <div class="col-6">
          <q-scroll-area
            :style="{ height: '165px' }"
            :thumb-style="{ borderRadius: '5px', background: '#9f9f9f', width: '2px', opacity: 1, right: 0 }"
          >
            <div class="single-page-markdown bg-white" v-html="dataMarkdown(description.text)"></div>
          </q-scroll-area>
        </div>
      </div>
      <h5 class="q-my-none q-mx-none q-pt-sm">Contacts</h5>
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <q-btn
            flat
            icon="add"
            color="white"
            text-color="black"
            small
            class="q-px-none q-mt-sm btn--no-hover q-mb-md"
            :ripple="false"
            size="11px"
            label="Add contact"
            @click="addContact"
          >
          </q-btn>
          <div class="row q-col-gutter-sm" v-for="(contact, key) in contacts" :key="key">
            <div class="col-5">
              <q-input
                dense
                bg-color="white"
                v-model="contacts[key].label"
                :rules="[(val) => !!val || 'Field is required', (val) => val.length < 40 || 'Max length is 40 symbols']"
                outlined
                label="Position"
              ></q-input>
            </div>
            <div class="col-6">
              <q-input
                dense
                bg-color="white"
                v-model="contacts[key].contact"
                :rules="emailRules"
                outlined
                label="Email"
              ></q-input>
            </div>
            <div class="col-1 text-center">
              <q-btn
                flat
                icon="remove"
                color="white"
                text-color="black"
                style="height: 40px"
                small
                class="q-px-none btn--no-hover"
                :ripple="false"
                size="11px"
                @click="removeContact(key)"
              >
                <q-tooltip>Remove contact</q-tooltip>
              </q-btn>
            </div>
          </div>
        </div>
      </div>
      <h5 class="q-my-none q-mx-none">Metadata</h5>
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <q-btn
            flat
            icon="add"
            color="white"
            text-color="black"
            small
            class="q-px-none q-mt-sm btn--no-hover q-mb-md"
            :ripple="false"
            size="11px"
            label="Add metadata"
            @click="addMetadata"
          >
          </q-btn>
          <div class="row q-col-gutter-sm" v-for="(entry, key) in metadata" :key="key">
            <div class="col-5">
              <q-input
                dense
                bg-color="white"
                v-model="metadata[key].label"
                :rules="[(val) => !!val || 'Field is required', (val) => val.length < 40 || 'Max length is 40 symbols']"
                outlined
                label="Label"
              ></q-input>
            </div>
            <div class="col-6">
              <q-input
                dense
                bg-color="white"
                v-model="metadata[key].value"
                :rules="[(val) => !!val || 'Field is required']"
                outlined
                label="Value"
              ></q-input>
            </div>
            <div class="col-1 text-center">
              <q-btn
                flat
                icon="remove"
                color="white"
                text-color="black"
                style="height: 40px"
                small
                class="q-px-none btn--no-hover"
                :ripple="false"
                size="11px"
                @click="removeMetadata(key)"
              >
                <q-tooltip>Remove metadata</q-tooltip>
              </q-btn>
            </div>
          </div>
        </div>
      </div>
      <h5 class="q-my-none q-mx-none">Links</h5>
      <div class="row q-col-gutter-lg q-mb-xs">
        <div class="col-6">
          <q-btn
            flat
            icon="add"
            color="white"
            text-color="black"
            small
            class="q-px-none q-mt-sm btn--no-hover q-mb-md"
            :ripple="false"
            size="11px"
            label="Add link"
            @click="addLink"
          >
          </q-btn>
          <div class="row q-col-gutter-sm q-mb-sm" v-for="(link, key) in links" :key="key">
            <div class="col-4">
              <q-input
                dense
                bg-color="white"
                v-model="links[key].label"
                :disable="
                  requiredLinks.includes(link.label) && links.filter((l) => l.label === link.label).length === 1
                "
                :rules="[(val) => !!val || 'Field is required', (val) => val.length < 40 || 'Max length is 40 symbols']"
                outlined
                label="Label"
              ></q-input>
            </div>
            <div class="col-3">
              <q-select
                v-model="links[key].type"
                :options="linkTypes"
                outlined
                bg-color="white"
                dense
                label="Type"
                emit-value
                multiple
                map-options
              ></q-select>
            </div>
            <div class="col-4">
              <q-input
                dense
                bg-color="white"
                v-model="links[key].url"
                :rules="[urlRule]"
                outlined
                label="URL"
              ></q-input>
            </div>
            <div class="col-1 text-center">
              <q-btn
                flat
                icon="remove"
                color="white"
                text-color="black"
                small
                :disable="
                  requiredLinks.includes(link.label) && links.filter((l) => l.label === link.label).length === 1
                "
                style="height: 40px"
                class="q-px-none btn--no-hover"
                :ripple="false"
                size="11px"
                @click="removeLink(key)"
              >
                <q-tooltip>Remove link</q-tooltip>
              </q-btn>
            </div>
          </div>
        </div>
      </div>
      <h5 class="q-my-none q-mx-none q-pb-sm">Displaying page sections</h5>
      <div class="row q-col-gutter-lg q-mb-md">
        <div class="col-4">
          <q-checkbox v-model="formData.pageSettings.onboardedApms" label="Show Onboarded APMs section"></q-checkbox>
        </div>
        <div class="col-4">
          <q-checkbox v-model="formData.pageSettings.contacts" label="Show Contacts section"></q-checkbox>
        </div>
        <div class="col-4">
          <q-checkbox v-model="formData.pageSettings.details" label="Show Details section"></q-checkbox>
        </div>
        <div class="col-4">
          <q-checkbox v-model="formData.pageSettings.typ" label="Show Technical yellow page section"></q-checkbox>
        </div>
        <div class="col-4">
          <q-checkbox v-model="formData.pageSettings.adrs" label="Show ADRs section"></q-checkbox>
        </div>
        <div class="col-4">
          <q-checkbox v-model="formData.pageSettings.metadata" label="Show Metadata section"></q-checkbox>
        </div>
      </div>
      <h5 class="q-my-none q-mx-none q-pb-sm">Feedback</h5>
      <div class="row q-col-gutter-lg q-mb-md">
        <div class="col-6">
          <q-select
            label="How would you like to receive feedback"
            v-model="feedbackType"
            dense
            outlined
            options-dense
            bg-color="white"
            :options="['Teams', 'Email', 'SNOW']"
          />
        </div>
        <div v-if="feedbackType === 'Teams'" class="col-6">
          <div class="row q-col-gutter-lg">
            <div class="col-6">
              <q-input v-model="feedback.channel" label="Channel name" bg-color="white" dense outlined bg-white />
            </div>
            <div class="col-6">
              <q-input v-model="feedback.title" label="Message title" bg-color="white" dense outlined bg-white />
            </div>
          </div>
        </div>
        <div v-if="feedbackType === 'Email'" class="col-6">
          <div class="row q-col-gutter-lg">
            <div class="col-6">
              <q-input
                v-model="feedback.email"
                label="Email"
                :rules="emailRules"
                bg-color="white"
                dense
                outlined
                bg-white
              />
            </div>
            <div class="col-6">
              <q-input v-model="feedback.title" label="Message title" bg-color="white" dense outlined bg-white />
            </div>
          </div>
        </div>
        <div v-if="feedbackType === 'SNOW'" class="col-6">
          <div class="row q-col-gutter-lg">
            <div class="col-6">
              <q-input v-model="feedback.group" label="Assignment Group" bg-color="white" dense outlined bg-white />
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <q-checkbox
            v-model="formData.toConsumeByDigitalService"
            label="Can be consumed by Digital Service (can Digital Service onboard to this service) "
          />
        </div>
        <div class="col-12">
          <q-checkbox
            v-if="formData.toConsumeByDigitalService"
            v-model="isContact"
            label="Did you contact the Engineering Portal team in regards of onboarding implementation?"
          />
        </div>
        <div class="col-4 q-mx-sm" v-if="formData.toConsumeByDigitalService">
          <q-select
            v-model="formData.kind"
            dense
            emit-value
            map-options
            bg-color="white"
            outlined
            options-dense
            :rules="[(val) => !!val || 'Field is required']"
            label="Kind *"
            :options="kinds"
          >
            <template v-slot:hint>
              <span
                >Please read the following Wiki article for details:
                <a href="https://wiki.swissre.com/pages/viewpage.action?pageId=775816223" target="_blank"
                  >What are the Kinds and how to use them - Platform Enabling Services - Confluence (swissre.com)</a
                ></span
              >
            </template>
          </q-select>
        </div>
      </div>
    </q-form>
    <div class="row q-col-gutter-lg q-mt-lg">
      <div class="col-12">
        <q-btn
          :loading="load"
          :disable="load || (formData.toConsumeByDigitalService && !isContact)"
          @click="submit"
          color="primary"
          size="md"
          >Submit</q-btn
        >
        <q-btn flat :ripple="false" @click="showConfirmDialog = true" class="btn--no-hover">Cancel</q-btn>
      </div>
    </div>
  </q-page>

  <q-dialog v-model="showConfirmDialog" class="single-service-page" persistent>
    <q-card style="min-width: 480px; border-radius: 0px">
      <q-toolbar class="shadow-2 q-py-sm q-px-md">
        <q-toolbar-title class="text-weight-bold">
          <strong>Cancel</strong>
        </q-toolbar-title>
        <q-btn
          icon="close"
          style="min-width: 20px"
          round
          flat
          class="btn--no-hover"
          @click="showConfirmDialog = false"
        ></q-btn>
      </q-toolbar>
      <q-card-section class="q-mx-md q-px-none q-pb-none" style="border-top: 2px solid var(--q-primary)">
        <p>Are you sure? All changes will be lost.</p>
      </q-card-section>
      <q-card-actions class="q-px-md q-pt-none q-pb-lg">
        <q-btn
          style="border-radius: 10px; text-transform: capitalize"
          color="primary"
          text-color="text-color"
          @click="onCancel"
          >Yes</q-btn
        >
        <q-btn
          style="border-radius: 10px; text-transform: capitalize"
          class="btn--no-hover"
          flat
          @click="showConfirmDialog = false"
          >No</q-btn
        >
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { marked } from 'marked';
import { v4 as uuidv4 } from 'uuid';
import { mapActions, mapGetters } from 'vuex';
import { SERVICES_ACTIONS, SERVICES_GETTERS } from '@/store/modules/services';
import ServiceSelect from '@/shared/components/ServiceSelect';
import apollo from '@/library/http/apollo';
import gql from 'graphql-tag';
import { getEnums } from './api';

const DRAFT_KEY = 'engprt-new-infra-service-draft';
const SAVE_INTERVAL = 60000;

export default {
  name: 'NewInfraService',
  components: {
    ServiceSelect,
  },
  data() {
    return {
      showConfirmDialog: false,
      cloudTypes: [],
      linkTypes: [],
      lifecycleStages: [],
      kinds: [],
      categories: [],
      contacts: [],
      metadata: [],
      requiredLinks: [],
      requiredDescriptions: [],
      links: [],
      isContact: false,
      feedbackType: undefined,
      feedback: {},
      apmIdError: false,
      load: false,
      draftInterval: null,
      draftCleared: false,
      formData: {
        digitalService: undefined,
        displayName: undefined,
        subtitle: undefined,
        category: undefined,
        subCategory: undefined,
        parentInfraService: null,
        descriptions: [],
        cloudType: undefined,
        serviceType: undefined,
        lifecycleStage: undefined,
        kind: null,
        toConsumeByDigitalService: false,
        pageSettings: {
          contacts: true,
          details: true,
          adrs: true,
          onboardedApms: true,
          typ: true,
          metadata: true,
        },
      },
    };
  },
  computed: {
    ...mapGetters('services', {
      catalog: SERVICES_GETTERS.ALL,
    }),
    emailRules() {
      const pattern = new RegExp('^\\S+@\\S+\\.\\S+$');
      return [(val) => !!val || 'Field is required', (val) => pattern.test(val) || 'Invalid email'];
    },
    urlRule() {
      const pattern = new RegExp(
        '^(https?:\\/\\/)?' +
          '([a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}|localhost|((\\d{1,3}\\.){3}\\d{1,3}))' +
          '(\\:\\d+)?' +
          '(\\/[^\\s]*)?$',
        'i',
      );
      return (val) => !val || pattern.test(val) || 'Invalid URL';
    },
    subCategories() {
      const category = this.catalog.find((cat) => cat.categoryId === this.formData.category);
      if (category && category.serviceCategories.length) {
        return category.serviceCategories.map((cat) => {
          const { name, categoryId } = cat;
          return { label: name, value: categoryId };
        });
      }
      return [];
    },

    parentInfraServices() {
      const infraServices = [{ displayName: 'None', serviceId: 'none' }];
      const category = this.catalog.find((cat) => cat.categoryId === this.formData.category);
      const allServices = this.catalog.map((cat) => cat.infraServices).flat();

      infraServices.push(...allServices);

      return infraServices
        .map((infra) => ({ label: infra.displayName, value: infra.serviceId }))
        .sort((a, b) => {
          const { label: labelA } = a;
          const { label: labelB } = b;
          if (labelA < labelB) {
            return -1;
          }
          if (labelA > labelB) {
            return 1;
          }
          return 0;
        });
    },
  },
  methods: {
    ...mapActions('services', {
      getCatalog: SERVICES_ACTIONS.GET_SERVICES,
    }),
    dataMarkdown(data) {
      return data ? marked(data) : '';
    },
    resetService() {
      this.formData.digitalService = undefined;
    },
    setService(service) {
      this.formData.digitalService = service;
      this.apmIdError = false;
    },
    addContact() {
      this.contacts.push({ label: '', contact: '' });
    },
    removeContact(index) {
      this.contacts.splice(index, 1);
    },
    addMetadata() {
      this.metadata.push({ label: '', value: '' });
    },
    removeMetadata(index) {
      this.metadata.splice(index, 1);
    },
    addLink() {
      this.links.push({ label: '', url: '', description: '', id: uuidv4() });
    },
    removeLink(index) {
      this.links.splice(index, 1);
    },
    onCancel() {
      this.clearDraft();
      this.draftCleared = true;
      this.$router.push('/services');
    },
    saveDraft() {
      try {
        const draft = {
          formData: this.formData,
          contacts: this.contacts,
          metadata: this.metadata,
          links: this.links,
          feedbackType: this.feedbackType,
          feedback: this.feedback,
          isContact: this.isContact,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (e) {
        console.error('Failed to save draft:', e);
      }
    },
    loadDraft() {
      try {
        const saved = localStorage.getItem(DRAFT_KEY);
        if (!saved) return false;

        const draft = JSON.parse(saved);

        if (draft.formData) {
          this.formData.digitalService = draft.formData.digitalService ?? this.formData.digitalService;
          this.formData.displayName = draft.formData.displayName ?? this.formData.displayName;
          this.formData.subtitle = draft.formData.subtitle ?? this.formData.subtitle;
          this.formData.category = draft.formData.category ?? this.formData.category;
          this.formData.subCategory = draft.formData.subCategory ?? this.formData.subCategory;
          this.formData.parentInfraService = draft.formData.parentInfraService ?? this.formData.parentInfraService;
          this.formData.cloudType = draft.formData.cloudType ?? this.formData.cloudType;
          this.formData.serviceType = draft.formData.serviceType ?? this.formData.serviceType;
          this.formData.lifecycleStage = draft.formData.lifecycleStage ?? this.formData.lifecycleStage;
          this.formData.kind = draft.formData.kind ?? this.formData.kind;
          this.formData.toConsumeByDigitalService =
            draft.formData.toConsumeByDigitalService ?? this.formData.toConsumeByDigitalService;

          if (draft.formData.pageSettings) {
            this.formData.pageSettings = { ...this.formData.pageSettings, ...draft.formData.pageSettings };
          }

          if (draft.formData.descriptions?.length && this.formData.descriptions?.length) {
            for (const savedDesc of draft.formData.descriptions) {
              const existing = this.formData.descriptions.find((d) => d.type === savedDesc.type);
              if (existing) {
                existing.text = savedDesc.text;
              }
            }
          }
        }

        if (draft.links?.length && this.links?.length) {
          for (const savedLink of draft.links) {
            const existing = this.links.find((l) => l.label === savedLink.label);
            if (existing) {
              existing.url = savedLink.url;
              existing.type = savedLink.type;
            }
          }

          const extraLinks = draft.links.filter(
            (savedLink) =>
              !this.requiredLinks.includes(savedLink.label) || !this.links.find((l) => l.label === savedLink.label),
          );
          for (const extraLink of extraLinks) {
            if (!this.links.find((l) => l.id === extraLink.id)) {
              this.links.push(extraLink);
            }
          }
        }

        if (draft.contacts?.length) {
          this.contacts = draft.contacts;
        }
        if (draft.metadata?.length) {
          this.metadata = draft.metadata;
        }
        if (draft.feedbackType !== undefined) {
          this.feedbackType = draft.feedbackType;
        }
        if (draft.feedback) {
          this.feedback = draft.feedback;
        }
        if (draft.isContact !== undefined) {
          this.isContact = draft.isContact;
        }

        return true;
      } catch (e) {
        console.error('Failed to load draft:', e);
        localStorage.removeItem(DRAFT_KEY);
        return false;
      }
    },
    clearDraft() {
      localStorage.removeItem(DRAFT_KEY);
      if (this.draftInterval) {
        clearInterval(this.draftInterval);
        this.draftInterval = null;
      }
    },
    startDraftAutoSave() {
      this.draftInterval = setInterval(() => {
        this.saveDraft();
      }, SAVE_INTERVAL);
    },
    submit() {
      this.$refs.form.validate().then(async (valid) => {
        if (!this.formData.digitalService) {
          this.apmIdError = true;
        } else {
          this.apmIdError = false;
        }
        if (valid) {
          this.load = true;
          const data = {
            apmid: this.formData.digitalService.apmId,
            displayName: this.formData.displayName,
            categoryId: this.formData.subCategory ? this.formData.subCategory : this.formData.category,
            parentService: this.formData.parentInfraService || null,
            cloudType: this.formData.cloudType,
            type: this.formData.serviceType,
            lifecycleStage: this.formData.lifecycleStage,
            descriptions: this.formData.descriptions,
            contacts: this.contacts.length ? this.contacts : null,
            metadata: this.metadata.length
              ? this.metadata.reduce((acc, entry) => {
                  if (entry.label && entry.value) acc[entry.label] = entry.value;
                  return acc;
                }, {})
              : null,
            links: this.links.filter((link) => link.url?.length),
            toConsumeByDigitalService: this.formData.toConsumeByDigitalService,
            kind: this.formData.kind,
            subtitle: this.formData.subtitle,
            pageSettings: this.formData.pageSettings,
          };
          if (this.feedbackType === 'Teams') {
            data.feedback = {
              type: 'teams',
              channel: this.feedback.channel,
              title: this.feedback.title,
            };
          } else if (this.feedbackType === 'Email') {
            data.feedback = {
              type: 'email',
              email: this.feedback.email,
              title: this.feedback.title,
            };
          } else if (this.feedbackType === 'SNOW') {
            data.feedback = {
              type: 'snow',
              group: this.feedback.group,
            };
          } else {
            data.feedback = null;
          }
          await apollo.apolloClient
            .mutate({
              mutation: gql`
                mutation createNewInfraservice($infraService: InfraServiceCreateDTO!) {
                  createNewInfraservice(infraService: $infraService)
                }
              `,
              variables: {
                infraService: data,
              },
            })
            .then(() => {
              this.clearDraft();
              this.draftCleared = true;
              this.$notify('positive', 'Success', 'Infrastructure service will be created.');
              this.$router.push('/services');
            })
            .catch((error) => {
              this.$notify('negative', 'Error', error.message);
            })
            .finally(() => {
              this.load = false;
            });
        }
      });
    },
  },
  async mounted() {
    this.$showLoading();
    if (!this.catalog.length) {
      await this.getCatalog({ filters: {}, setServiceDesign: false });
    }
    this.categories = this.catalog.map((cat) => {
      const { name, categoryId } = cat;
      return { label: name, value: categoryId };
    });
    if (this.$route.params?.category) {
      this.formData.category = this.$route.params.category;
    }
    try {
      const kinds = await getEnums('ERegistrationKind');
      this.kinds = kinds.map((i) => ({ value: i.key, label: i.value }));
      const states = await getEnums('EServiceState');
      this.lifecycleStages = states.map((i) => ({ value: i.key, label: i.value }));
      const cloudTypes = await getEnums('ECloudType');
      this.cloudTypes = cloudTypes.map((i) => ({ value: i.key, label: i.value }));
      const linkTypes = await getEnums('ELinkType');
      this.linkTypes = linkTypes.map((i) => ({ value: i.key, label: i.value }));
      const links = await getEnums('EInfraServiceLinkTitle');
      this.requiredLinks = links.map((i) => i.value);
      this.links = this.requiredLinks.map((link) => ({ id: uuidv4(), label: link, url: undefined }));
      const descriptions = await getEnums('EInfraServiceDescriptionTitle');
      this.requiredDescriptions = descriptions.map((i) => i.value);
      this.formData.descriptions = this.requiredDescriptions.map((descr) => ({
        id: uuidv4(),
        type: descr,
        text: null,
      }));

      this.loadDraft();
    } catch (error) {
      this.$notify('negative', 'Error', error.message);
    } finally {
      this.$hideLoading();
    }

    this.startDraftAutoSave();
  },
  beforeUnmount() {
    if (this.draftInterval) {
      clearInterval(this.draftInterval);
    }

    if (this.draftCleared) return;
    const hasInput =
      this.formData.displayName ||
      this.formData.subtitle ||
      this.formData.digitalService ||
      this.formData.category ||
      this.contacts.length ||
      this.metadata.length ||
      this.formData.descriptions?.some((d) => d.text);

    if (hasInput) {
      this.saveDraft();
    }
  },
};
</script>

<style scoped>
.exist-service-holder {
  background-color: #fff;
}
.single-page-markdown {
  background-color: white;
  min-height: 145px;
  padding: 15px;
  border-radius: 4px;
}
.service-select-hint {
  position: absolute;
  font-size: 11px;
  bottom: -30px;
  line-height: 1;
  color: #707072;
}
</style>

<style lang="scss">
.new-infra-apm-select {
  span.error {
    left: 0;
    bottom: -18px;
  }
  .service-select-hint {
    left: 0;
    bottom: -25px;
  }
}
</style>
